import React from 'react';
import { Box, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

import TextField, { TextArea } from 'components/CustomFields';
import CustomButton from 'components/CustomButton';
import useResumeStore from './store';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    overflow: 'hidden',
  },
  formContent: {
    display: 'flex',
    gap: theme.spacing(6),
    padding: theme.spacing(6),
    overflowY: 'auto',
    flex: 1,
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.neutral['clr-300'],
      borderRadius: '3px',
    },
  },
  leftSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
    flex: 1,
  },
  rightSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    flex: 1,
  },
  fieldRow: {
    display: 'flex',
    gap: theme.spacing(4),
  },
  enhanceButton: {
    background: 'linear-gradient(90deg, #1961BF 0%, #0D47A1 100%)',
    color: theme.palette.shades['clr-white-900'],
    borderRadius: '20px',
    padding: theme.spacing(2, 5),
    cursor: 'not-allowed',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    transition: 'opacity 0.3s ease',
    width: 'fit-content',
    opacity: 0.6,
    position: 'relative',
  },
  comingSoonTag: {
    background: theme.palette.warning?.main || '#FF9800',
    color: theme.palette.shades?.['clr-white-900'] || '#fff',
    borderRadius: '4px',
    padding: theme.spacing(0.25, 1),
    fontSize: '10px',
    fontWeight: 700,
    lineHeight: '14px',
    whiteSpace: 'nowrap',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(4, 6),
    borderTop: `1px solid ${theme.palette.neutral['clr-200']}`,
  },
}));

const HeadingsForm = ({ onNext }) => {
  const classes = useStyles();

  const [headings, setHeadings] = useResumeStore(
    (state) => [state.headings, state.setHeadings]
  );

  const handleChange = (field) => (e) => {
    setHeadings({ [field]: e.target.value });
  };

  const isNextDisabled = !headings.firstName?.trim() ||
    !headings.surname?.trim() ||
    !headings.profession?.trim() ||
    !headings.city?.trim() ||
    !headings.phone?.trim() ||
    !headings.email?.trim() ||
    !headings.linkedin?.trim() ||
    !headings.summary?.trim();

  return (
    <Box className={classes.container}>
      <Box className={classes.formContent}>
        <Box className={classes.leftSection}>
          <TextField
            label='First Name'
            placeholder='Saanvi'
            value={headings.firstName}
            onChange={handleChange('firstName')}
            required
          />

          <TextField
            label='Last Name'
            placeholder='Patel'
            value={headings.surname}
            onChange={handleChange('surname')}
            required
          />

          <TextField
            label='Profession'
            placeholder='Retail Sales Associate'
            value={headings.profession}
            onChange={handleChange('profession')}
            required
          />

          <TextField
            label='City'
            placeholder='New Delhi'
            value={headings.city}
            onChange={handleChange('city')}
            required
          />

          <Box className={classes.fieldRow}>
            <Box flex={1}>
              <TextField
                label='Phone'
                placeholder='+91 22 1234 5677'
                value={headings.phone}
                onChange={handleChange('phone')}
                required
              />
            </Box>
            <Box flex={1}>
              <TextField
                label='Email'
                placeholder='saanvipatel@sample.in'
                value={headings.email}
                onChange={handleChange('email')}
                required
              />
            </Box>
          </Box>

          <TextField
            label='Linkedin'
            placeholder='ex. linkedin.com/in/yourname'
            value={headings.linkedin}
            onChange={handleChange('linkedin')}
            required
          />

          <TextField
            label='Git Hub'
            placeholder='ex. github.com/yourname'
            value={headings.github}
            onChange={handleChange('github')}
          />

          <TextField
            label='Portfolio'
            placeholder='ex. example.squarespace.com'
            value={headings.portfolio}
            onChange={handleChange('portfolio')}
          />
        </Box>

        <Box className={classes.rightSection}>
          <Typography variant='h6-semiBold' color='neutral.clr-900'>
            Summary
          </Typography>
          <TextArea
            placeholder='Write a brief professional summary...'
            value={headings.summary}
            onChange={handleChange('summary')}
            minRows={12}
            maxRows={16}
          />
          <Box
            component='button'
            className={classes.enhanceButton}
            disabled
          >
            <Typography variant='body01-bold' color='shades.clr-white-900'>
              Enhance with AI
            </Typography>
            <Box component='span' className={classes.comingSoonTag}>
              Coming Soon
            </Box>
          </Box>
        </Box>
      </Box>

      <Box className={classes.footer}>
        <Typography variant='body02-bold' color='warning.main'>
          Note: Your data will be lost if you leave this page.
        </Typography>
        <CustomButton
          size='medium'
          onClick={onNext}
          disabled={isNextDisabled}
          sx={{ width: 'fit-content' }}
        >
          Next: Work Experience
        </CustomButton>
      </Box>
    </Box>
  );
};

export default HeadingsForm;
